/**
 * Currency and Localization Utilities
 * Handles country detection, currency mapping, and formatting
 */

import { getLocaleForCurrency, getCurrencySymbol as getConfigCurrencySymbol } from './currencyConfig';

// Country to Currency mapping (only supported countries)
export const COUNTRY_CURRENCY_MAP = {
  'US': 'USD',
  'GB': 'GBP',
  'CA': 'CAD',
  'AU': 'AUD',
  'NZ': 'NZD',
  'IE': 'EUR',
  'EU': 'EUR',
};

// Currency symbols for quick display
export const CURRENCY_SYMBOLS = {
  'USD': '$',
  'GBP': '£',
  'EUR': '€',
  'CAD': '$',
  'AUD': 'A$',
  'NZD': 'NZ$',
};

// Default currency fallback
export const DEFAULT_CURRENCY = 'GBP';
export const DEFAULT_COUNTRY = 'GB';

/**
 * Detect user's country using timezone (no external API calls)
 * Uses browser's timezone to guess the country
 */
export const detectCountry = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log('🌍 Detected timezone:', timezone);
    
    // Map timezones to countries
    if (timezone.includes('America/New_York') || timezone.includes('America/Chicago') || 
        timezone.includes('America/Denver') || timezone.includes('America/Los_Angeles') ||
        timezone.includes('America/Phoenix') || timezone.includes('America/Anchorage') ||
        timezone.includes('America/Honolulu')) {
      return 'US';
    }
    if (timezone.includes('Europe/London')) {
      return 'GB';
    }
    if (timezone.includes('America/Toronto') || timezone.includes('America/Vancouver') ||
        timezone.includes('America/Montreal') || timezone.includes('America/Winnipeg') ||
        timezone.includes('America/Edmonton') || timezone.includes('America/Halifax')) {
      return 'CA';
    }
    if (timezone.includes('Australia/')) {
      return 'AU';
    }
    if (timezone.includes('Europe/Dublin')) {
      return 'IE';
    }
    if (timezone.includes('Pacific/Auckland') || timezone.includes('Pacific/Chatham')) {
      return 'NZ';
    }
    if (timezone.includes('Europe/')) {
      return 'EU';
    }
    
    // Default fallback
    console.log('🌍 Using default country:', DEFAULT_COUNTRY, '(timezone not recognized)');
    return DEFAULT_COUNTRY;
  } catch (error) {
    console.warn('⚠️ Timezone detection failed:', error);
    return DEFAULT_COUNTRY; // Safe default
  }
};

/**
 * Get currency code for a country
 */
export const getCurrencyForCountry = (countryCode) => {
  if (!countryCode) return DEFAULT_CURRENCY;
  const currency = COUNTRY_CURRENCY_MAP[countryCode.toUpperCase()] || DEFAULT_CURRENCY;
  return currency;
};

export const formatCurrency = (amount, currency = DEFAULT_CURRENCY, locale = null) => {
  if (typeof amount !== 'number' || isNaN(amount)) {
    const symbol = getConfigCurrencySymbol(currency) || CURRENCY_SYMBOLS[currency] || currency;
    return `${symbol}0.00`;
  }

  // Use locale from config if not provided
  const formatLocale = locale || getLocaleForCurrency(currency);

  try {
    return new Intl.NumberFormat(formatLocale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    // Fallback to simple formatting
    const symbol = getConfigCurrencySymbol(currency) || CURRENCY_SYMBOLS[currency] || currency;
    return `${symbol}${amount.toFixed(2)}`;
  }
};

/**
 * Format a monetary amount when CurrencyProvider is unavailable or for explicit currency.
 * Prefer useCurrency().format() inside components wrapped by CurrencyProvider.
 */
export const formatMoney = (amount, currency = DEFAULT_CURRENCY) => {
  const n = typeof amount === 'number' && !Number.isNaN(amount) ? amount : Number(amount);
  return formatCurrency(Number.isNaN(n) ? 0 : n, currency);
};

/**
 * Get currency symbol
 */
export const getCurrencySymbol = (currency = DEFAULT_CURRENCY) => {
  // Try config first, then fallback to CURRENCY_SYMBOLS
  return getConfigCurrencySymbol(currency) || CURRENCY_SYMBOLS[currency] || currency;
};

/**
 * Convert amount to smallest currency unit (cents, pence, etc.)
 */
export const toSmallestUnit = (amount, currency = DEFAULT_CURRENCY) => {
  if (typeof amount !== 'number' || isNaN(amount)) return 0;
  
  // Most currencies use 2 decimal places
  // Some like JPY don't, but we'll handle that if needed
  return Math.round(amount * 100);
};

/**
 * Convert from smallest currency unit to standard amount
 */
export const fromSmallestUnit = (amount, currency = DEFAULT_CURRENCY) => {
  if (typeof amount !== 'number' || isNaN(amount)) return 0;
  return amount / 100;
};

/**
 * Get locale for a country (for date/time formatting)
 */
export const getLocaleForCountry = (countryCode) => {
  const localeMap = {
    'US': 'en-US',
    'GB': 'en-GB',
    'CA': 'en-CA',
    'AU': 'en-AU',
    'NZ': 'en-NZ',
    'IE': 'en-IE',
    'EU': 'en-IE',
  };
  return localeMap[countryCode] || 'en-GB';
};

